import { useContext, useEffect, useRef, useState } from "react";
import {
  NumericKeypadContext,
  type NumericKeypadMode,
  type NumericKeypadQuickAction,
} from "./NumericKeypadProvider";

// O dock do teclado virtual só é exibido em telas >= lg (1024px).
// Em viewports menores (mobile), precisamos liberar o teclado nativo do
// dispositivo, caso contrário o campo fica `readOnly` sem nenhum keypad visível.
function useShouldUseVirtualKeypad() {
  const getMatch = () => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const hasDock = window.matchMedia("(min-width: 1024px)").matches;
    return coarse && hasDock;
  };
  const [match, setMatch] = useState<boolean>(getMatch);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mqlCoarse = window.matchMedia("(pointer: coarse)");
    const mqlWidth = window.matchMedia("(min-width: 1024px)");
    const update = () => setMatch(getMatch());
    mqlCoarse.addEventListener?.("change", update);
    mqlWidth.addEventListener?.("change", update);
    window.addEventListener("resize", update);
    return () => {
      mqlCoarse.removeEventListener?.("change", update);
      mqlWidth.removeEventListener?.("change", update);
      window.removeEventListener("resize", update);
    };
  }, []);
  return match;
}

export function useNumericKeypadField(params: {
  id: string;
  label: string;
  mode: NumericKeypadMode;
  getCents: () => number;
  setCents: (cents: number) => void;
  onClear?: () => void;
  maxCents?: number;
  quickActions?: NumericKeypadQuickAction[];
}) {
  const ctx = useContext(NumericKeypadContext);
  const useVirtualKeypad = useShouldUseVirtualKeypad();

  // Mantém referências estáveis para evitar unregister/register em cada render
  // (isso limpava o activeFieldId e fazia o teclado "parar de funcionar").
  const getCentsRef = useRef(params.getCents);
  const setCentsRef = useRef(params.setCents);
  const onClearRef = useRef(params.onClear);
  const labelRef = useRef(params.label);
  const modeRef = useRef(params.mode);
  const maxCentsRef = useRef(params.maxCents);
  const quickActionsRef = useRef(params.quickActions);

  // Atualiza refs sempre que props mudarem
  useEffect(() => {
    getCentsRef.current = params.getCents;
    setCentsRef.current = params.setCents;
    onClearRef.current = params.onClear;
    labelRef.current = params.label;
    modeRef.current = params.mode;
    maxCentsRef.current = params.maxCents;
    quickActionsRef.current = params.quickActions;
  });

  useEffect(() => {
    if (!ctx) return;
    // Registra uma vez (por ctx+id) com wrappers estáveis.
    ctx.registerField({
      id: params.id,
      label: labelRef.current,
      mode: modeRef.current,
      getCents: () => getCentsRef.current(),
      setCents: (cents) => setCentsRef.current(cents),
      onClear: () => onClearRef.current?.(),
      maxCents: maxCentsRef.current,
      quickActions: quickActionsRef.current,
    });
    return () => ctx.unregisterField(params.id);
  }, [ctx, params.id]);

  // Mantém metadados do campo atualizados sem derrubar o campo ativo.
  useEffect(() => {
    if (!ctx) return;
    ctx.registerField({
      id: params.id,
      label: labelRef.current,
      mode: modeRef.current,
      getCents: () => getCentsRef.current(),
      setCents: (cents) => setCentsRef.current(cents),
      onClear: () => onClearRef.current?.(),
      maxCents: maxCentsRef.current,
      quickActions: quickActionsRef.current,
    });
  }, [ctx, params.id, params.label, params.mode, params.maxCents, params.quickActions]);

  const isActive = !!ctx && ctx.activeFieldId === params.id;
  const activate = () => ctx?.setActiveFieldId(params.id);

  return {
    isActive,
    activate,
    // Só bloqueia o teclado nativo quando o keypad virtual está visível
    // (touchscreen + viewport >= lg). Em mobile, libera o teclado do device.
    readOnly: useVirtualKeypad,
    inputMode: useVirtualKeypad ? ("none" as const) : ("decimal" as const),
  };
}
